import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

interface OnboardingProps {
  /** Called when the user finishes onboarding (clicks "Get Started" on the
   *  last slide, or skips). The parent persists the completion flag. */
  onComplete: () => void;
}

/**
 * Mobile/APK first-launch onboarding. Four full-bleed slides with the
 * IdeaVerdict brand, swipeable + tap-to-advance, with a Skip option.
 *
 * All copy is i18n-driven so it appears in whatever language the user has
 * selected (default DE for the APK build per capacitor.config.ts +
 * src/i18n/index.ts).
 */
export default function Onboarding({ onComplete }: OnboardingProps) {
  const { t } = useTranslation();
  const [slide, setSlide] = useState(0);

  // Each slide has icon, title, body. Translation keys fall back to English
  // strings so the component still works if a locale hasn't been translated.
  const slides = [
    {
      icon: 'ri-flashlight-fill',
      title: t('onboard.s1.title', 'Welcome to IdeaVerdict'),
      body: t('onboard.s1.body', 'An AI verdict on your business idea — before you build.'),
    },
    {
      icon: 'ri-scales-3-fill',
      title: t('onboard.s2.title', 'Brutally honest verdicts'),
      body: t('onboard.s2.body', 'No hype, no flattery. Real market data, real competitors, real risks.'),
    },
    {
      icon: 'ri-file-text-fill',
      title: t('onboard.s3.title', 'Builder Reports as PDFs'),
      body: t('onboard.s3.body', 'Generate 25–75 page reports with full architecture, financials and go-to-market strategy.'),
    },
    {
      icon: 'ri-rocket-2-fill',
      title: t('onboard.s4.title', 'Ready when you are'),
      body: t('onboard.s4.body', 'Sign up and get your first verdict in under a minute.'),
    },
  ];

  const isLast = slide === slides.length - 1;
  const next = () => (isLast ? onComplete() : setSlide((s) => s + 1));
  const back = () => setSlide((s) => Math.max(0, s - 1));

  // Touch swipe — tracks horizontal drag and advances/retreats on release.
  const touchStart = useState<number | null>(null);
  const [startX, setStartX] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => setStartX(e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX === null) return;
    const dx = e.changedTouches[0].clientX - startX;
    if (dx < -40) next();
    else if (dx > 40) back();
    setStartX(null);
  };
  void touchStart;

  const cur = slides[slide];

  return (
    <div
      className="fixed inset-0 z-[1000] flex flex-col bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 text-white"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar — Logo + Skip */}
      <div className="flex items-center justify-between px-5 pt-[max(env(safe-area-inset-top),16px)] pb-2">
        <Logo variant="full" inverted />
        <button
          onClick={onComplete}
          className="text-sm font-medium text-white/80 hover:text-white px-3 py-1.5 rounded-lg active:bg-white/10 transition-colors cursor-pointer"
        >
          {t('onboard.skip', 'Skip')}
        </button>
      </div>

      {/* Slide content (vertically centered, fills space) */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <div className="w-24 h-24 rounded-3xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-8 shadow-2xl">
          <i className={`${cur.icon} text-5xl text-white`}></i>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4 max-w-[320px]">
          {cur.title}
        </h1>
        <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-[340px]">
          {cur.body}
        </p>
      </div>

      {/* Pagination dots */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all ${i === slide ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}
          />
        ))}
      </div>

      {/* Bottom CTA bar */}
      <div className="px-5 pb-[max(env(safe-area-inset-bottom),24px)] pt-2">
        <button
          onClick={next}
          className="w-full py-4 bg-white text-emerald-700 rounded-2xl font-bold text-base shadow-lg active:scale-[0.98] transition-transform cursor-pointer flex items-center justify-center gap-2"
        >
          {isLast
            ? <>{t('onboard.cta.start', 'Get started')} <i className="ri-arrow-right-line"></i></>
            : <>{t('onboard.cta.next', 'Next')} <i className="ri-arrow-right-line"></i></>
          }
        </button>
      </div>
    </div>
  );
}
